/**
 * CR158: Run Status Debug Header
 * Shows last step update, running step info, and stale detection
 */

import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Clock, AlertTriangle, Activity, CheckCircle2 } from "lucide-react";
import { formatDistanceToNow, differenceInMinutes } from "date-fns";

interface BatchRunStep {
  id: string;
  step_key: string;
  step_name: string;
  status: string;
  updated_at?: string;
  started_at?: string | null;
  finished_at?: string | null;
}

interface RunStatusDebugHeaderProps {
  steps: BatchRunStep[];
  runStatus: string;
}

const STALE_THRESHOLD_MINUTES = 10;

export function RunStatusDebugHeader({ steps, runStatus }: RunStatusDebugHeaderProps) {
  // Only show for RUNNING status
  if (runStatus !== "RUNNING") return null;

  const now = new Date();

  // Find the most recently updated step
  const stepsWithDates = steps
    .filter((s) => s.updated_at)
    .map((s) => ({
      ...s,
      updatedAtDate: new Date(s.updated_at!),
    }))
    .sort((a, b) => b.updatedAtDate.getTime() - a.updatedAtDate.getTime());

  const lastUpdatedStep = stepsWithDates[0];

  // Find running steps
  const runningSteps = steps.filter((s) => s.status === "RUNNING");

  // Check for stale steps
  const staleSteps = runningSteps.filter((s) => {
    if (!s.updated_at) return false;
    const updatedAt = new Date(s.updated_at);
    const minutesAgo = differenceInMinutes(now, updatedAt);
    return minutesAgo >= STALE_THRESHOLD_MINUTES;
  });

  const hasStaleSteps = staleSteps.length > 0;

  // Calculate time since last update
  const lastUpdateTime = lastUpdatedStep?.updatedAtDate;
  const minutesSinceLastUpdate = lastUpdateTime 
    ? differenceInMinutes(now, lastUpdateTime) 
    : null;

  return (
    <TooltipProvider>
      <div className={`flex items-center gap-3 px-3 py-2 rounded-md border text-sm ${
        hasStaleSteps 
          ? "bg-orange-500/10 border-orange-500/30" 
          : "bg-blue-500/10 border-blue-500/30"
      }`}>
        <div className="flex items-center gap-1.5">
          <Activity className={`h-3.5 w-3.5 ${hasStaleSteps ? "text-orange-500" : "text-blue-500"}`} />
          <span className="font-medium text-muted-foreground">Run Status</span>
        </div>
        
        <div className="h-4 w-px bg-border" />
        
        {/* Last step update */}
        <div className="flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-muted-foreground">Last update:</span>
          {lastUpdateTime ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className={`font-mono text-xs ${
                  minutesSinceLastUpdate && minutesSinceLastUpdate >= STALE_THRESHOLD_MINUTES
                    ? "text-orange-600 font-medium"
                    : ""
                }`}>
                  {formatDistanceToNow(lastUpdateTime, { addSuffix: true })}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-mono text-xs">{lastUpdateTime.toISOString()}</p>
                <p className="text-muted-foreground">Step: {lastUpdatedStep?.step_name}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <span className="text-muted-foreground text-xs">Unknown</span>
          )}
        </div>

        <div className="h-4 w-px bg-border" />

        {/* Running step */}
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Running:</span>
          {runningSteps.length > 0 ? (
            <div className="flex items-center gap-1">
              {runningSteps.map((step) => {
                const stepUpdatedAt = step.updated_at ? new Date(step.updated_at) : null;
                const isStale = stepUpdatedAt 
                  ? differenceInMinutes(now, stepUpdatedAt) >= STALE_THRESHOLD_MINUTES 
                  : false;
                
                return (
                  <Tooltip key={step.id}>
                    <TooltipTrigger asChild>
                      <Badge 
                        variant={isStale ? "destructive" : "default"} 
                        className="text-xs cursor-help"
                      >
                        {step.step_key}
                        {stepUpdatedAt && (
                          <span className="ml-1 opacity-80">
                            ({differenceInMinutes(now, stepUpdatedAt)}m ago)
                          </span>
                        )}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="font-medium">{step.step_name}</p>
                      {stepUpdatedAt && (
                        <p className="text-muted-foreground text-xs">
                          Updated: {stepUpdatedAt.toISOString()}
                        </p>
                      )}
                      {isStale && (
                        <p className="text-orange-500 text-xs mt-1">
                          ⚠️ Stale (no updates in {STALE_THRESHOLD_MINUTES}+ minutes)
                        </p>
                      )}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          ) : (
            <Badge variant="outline" className="text-xs">None</Badge>
          )}
        </div>

        <div className="h-4 w-px bg-border" />

        {/* Stale indicator */}
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Stale:</span>
          {hasStaleSteps ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="destructive" className="text-xs gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Yes ({staleSteps.length})
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-medium">Stale Steps Detected</p>
                <p className="text-muted-foreground text-xs">
                  {staleSteps.length} step(s) have not updated in {STALE_THRESHOLD_MINUTES}+ minutes.
                </p>
                <p className="text-orange-500 text-xs mt-1">
                  Use "Cancel Run" → "Force Stop" to recover.
                </p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Badge variant="outline" className="text-xs gap-1">
              <CheckCircle2 className="h-3 w-3 text-green-500" />
              No
            </Badge>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

/**
 * Check if a batch run has stale steps (for blocking logic)
 */
export function hasStaleRunningSteps(steps: BatchRunStep[]): boolean {
  const now = new Date();
  return steps.some((s) => {
    if (s.status !== "RUNNING") return false;
    if (!s.updated_at) return false;
    const updatedAt = new Date(s.updated_at);
    const minutesAgo = differenceInMinutes(now, updatedAt);
    return minutesAgo >= STALE_THRESHOLD_MINUTES;
  });
}

/**
 * Check if a batch run has any non-stale running steps (for delete blocking)
 */
export function hasActiveNonStaleSteps(steps: BatchRunStep[]): boolean {
  const now = new Date();
  return steps.some((s) => {
    if (s.status !== "RUNNING" && s.status !== "ENQUEUED") return false;
    if (s.status === "ENQUEUED") return true; // ENQUEUED is always "active"
    if (!s.updated_at) return true; // No update time = assume active
    const updatedAt = new Date(s.updated_at);
    const minutesAgo = differenceInMinutes(now, updatedAt);
    return minutesAgo < STALE_THRESHOLD_MINUTES;
  });
}
